import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Menu, X, RefreshCw } from "lucide-react";
import { Icon } from "@shopify/polaris";
import { brandColors } from "config/colors.config";
import { CompanySelector } from "./CompanySelector";
import { MicrosoftSecretExpiryBanner } from "./MicrosoftSecretExpiryBanner";
import { PHONE_BAR_HEIGHT, PHONE_BAR_HEIGHT_VAR } from "./phone-bar-offset";
import { PhoneBottomBar } from "./phone-bottom-bar";
import { TOPBAR_ICON_BUTTON_TOUCH_TARGET } from "./topbar-icon-button";
import useIsTyping from "./use-is-typing";
import CartBadge from "features/document-cart/CartBadge";
import HeaderBadge from "components/header-badge/header-badge";
import { AssistantLauncher } from "features/assistant/assistant-launcher";
import useIsPhone from "features/files/use-is-phone";
import CartDrawer from "features/document-cart/CartDrawer";
import { useDocumentCartSocket } from "features/document-cart/use-document-cart-socket";
import { isBelowDesktop } from "utils/breakpoints";
import { isUpdateAvailable } from "utils/versionCheck";
import { isSameOrBelow } from "containers/settings/settings-sections";
import { useTranslation } from "react-i18next";
import "./DashboardLayout.scss";

/** The sidebar groups from top to bottom, with the translation key of each title. */
// The sidebar's groups in order. The personal cabinet sits on its own lighter panel, so the
// person's own sections read apart from the company's pages and modules below it.
const MENU_GROUPS = [
  { group: "cabinet", titleKey: "cabinet_group", isPersonal: true },
  { group: "company", titleKey: "company", isPersonal: false },
  { group: "modules", titleKey: "modules_group", isPersonal: false },
] as const;

type MenuGroup = (typeof MENU_GROUPS)[number]["group"];

export interface MenuItem {
  id: number | string;
  /** Which sidebar group the item sits in. */
  group: MenuGroup;
  url: string;
  label: string;
  icon: any;
  isVisibleOnlyFor?: string[];
  /** Shown to a group visitor, who otherwise sees no menu items at all. */
  isVisibleToGroupVisitor?: boolean;
  /** Hidden from a guest member, who has only «About me» in the cabinet and the news and «About the company». */
  notForGuestMember?: boolean;
  badge?: number;
  hasPersonal?: boolean;
  /** A small amber dot: something in this section waits for the person, no number. */
  needsAttention?: boolean;
  /** Other path prefixes that highlight this item (a module's sibling pages). */
  alsoActiveFor?: string[];
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  menuItems: MenuItem[];
  role?: any;
  companyId?: string;
  onCompanyChange: (id: string) => void;
  onVisitGroupCompany: (id: string) => void;
  onNavigationToggle?: () => void;
  userMenuMarkup: React.ReactNode;
  notificationCount: number;
  onNotificationClick: () => void;
  selectedCompany?: { label: string; value: string };
  /**
   * The person only visits this company (not a member): a menu of news and
   * "About", and none of the personal tools, notifications or chat that need
   * membership.
   */
  isGroupVisitor?: boolean;
  /** The button that opens the online-users sheet; it shows below 1024px, where there is no rail. */
  onlineUsersButton?: React.ReactNode;
}

export function DashboardLayout({
  children,
  menuItems,
  role,
  companyId,
  onCompanyChange,
  onVisitGroupCompany,
  onNavigationToggle,
  userMenuMarkup,
  notificationCount,
  onNotificationClick,
  selectedCompany,
  isGroupVisitor = false,
  onlineUsersButton,
}: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useDocumentCartSocket(isGroupVisitor ? undefined : companyId);

  const [updateAvailable, setUpdateAvailable] = useState(() => isUpdateAvailable());

  useEffect(() => {
    const handler = () => setUpdateAvailable(true);
    window.addEventListener("appUpdateAvailable", handler);
    return () => window.removeEventListener("appUpdateAvailable", handler);
  }, []);

  const handleRefresh = () => {
    if ("caches" in window) {
      caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
    }
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString());
    window.location.href = url.toString();
  };

  // The bottom bar belongs to the phone width (under Tailwind's md); the sidebar is a
  // drawer already from lg down.
  const isPhone = useIsPhone();

  // Below Tailwind's `lg` the sidebar is a drawer over the page. The drawer starts closed and
  // the desktop sidebar open, already on the first frame (starting from one and switching in
  // an effect shows the wrong one for a moment).
  const [isMobile, setIsMobile] = useState(isBelowDesktop);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => !isBelowDesktop());

  useEffect(() => {
    const onResize = () => {
      const isDrawer = isBelowDesktop();
      setIsMobile(isDrawer);
      setIsSidebarOpen(!isDrawer);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const closeDrawer = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleMenuClick = (url: string) => {
    navigate(url);
    closeDrawer();
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
    if (onNavigationToggle) {
      onNavigationToggle();
    }
  };


  const permissionsGroups: string[] = useMemo(
    () => role?.permissionsgroups ?? [],
    [role?.permissionsgroups]
  );
  const isMaster = permissionsGroups.includes("Master");

  // Filter menu items based on permissions
  const isGuestMember = role?.isUserGuest === true;
  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter(
        ({ isVisibleOnlyFor, isVisibleToGroupVisitor, notForGuestMember }) => {
          if (isGroupVisitor) return Boolean(isVisibleToGroupVisitor);
          if (notForGuestMember && isGuestMember) return false;
          if (!isVisibleOnlyFor) return true;
          return isVisibleOnlyFor.some((permission) =>
            permissionsGroups.includes(permission)
          );
        }
      ),
    [menuItems, permissionsGroups, isGroupVisitor, isGuestMember]
  );

  // Determine active menu item based on current URL
  const getActiveItem = () => {
    const currentPath = location.pathname;

    // "/assets" owns "/assets/12" but not "/assetstypes" (a settings page).
    const isUnder = (url: string) => isSameOrBelow(currentPath, url);

    const activeItem = menuItems.find((item) => {
      if (item.url.includes("/desk/") && currentPath.includes("/desk/")) {
        return true;
      }
      if (item.alsoActiveFor?.some(isUnder)) {
        return true;
      }
      return isUnder(item.url);
    });
    return activeItem?.id || null;
  };

  const activeItem = getActiveItem();
  // The bar steps aside while the keyboard is up, so it does not eat the little room left.
  const isTyping = useIsTyping();
  const showPhoneBar =
    isPhone && Boolean(companyId) && !isGroupVisitor && !isGuestMember && !isTyping;

  // Published like --presence-rail-width, so the presence rail, the mini chat, the selection
  // bars and the call card can keep clear of the bar without knowing about this component.
  useEffect(() => {
    if (!showPhoneBar) return undefined;
    document.documentElement.style.setProperty(PHONE_BAR_HEIGHT_VAR, PHONE_BAR_HEIGHT);
    return () => {
      document.documentElement.style.removeProperty(PHONE_BAR_HEIGHT_VAR);
    };
  }, [showPhoneBar]);
  const unreadKudosLabel = t("unread_kudos", {
    defaultValue: "You have unread kudos",
  });
  const needsAttentionLabel = t("needs_attention");
  const notificationsLabel =
    notificationCount > 0 ? t("header_my_work_title", { count: notificationCount }) : t("tasks");

  return (
    <div className="flex h-screen supports-[height:100dvh]:h-dvh bg-gray-50">
      {/* Left Sidebar. Closed, it is zero wide and also `invisible`, which takes its buttons out of the
          tab order and away from screen readers; the transition makes it turn hidden only after it has
          slid shut, and visible again at the start of opening. A drawer item that has focus when the
          drawer closes loses it to <body>; that is accepted, because choosing an item changes the route,
          and a route change moves focus anyway. */}
      <aside
        className={`dashboard-sidebar bg-[${
          brandColors.primary.base
        }] text-white flex flex-col transition-all duration-300 ${
          isSidebarOpen
            ? isMobile
              ? "w-64 fixed inset-y-0 left-0 z-50"
              : "w-64"
            : "invisible w-0 overflow-hidden"
        }`}
        style={{ backgroundColor: brandColors.primary.base }}
      >
        <CompanySelector
          companyId={companyId}
          selectedCompanyLabel={selectedCompany?.label}
          onCompanyChange={(id) => {
            onCompanyChange(id);
            closeDrawer();
          }}
          onVisitGroupCompany={(id) => {
            onVisitGroupCompany(id);
            closeDrawer();
          }}
        />

        {/* Navigation Menu: a title above each group that has a visible item */}
        <nav className="flex-1 overflow-y-auto py-4">
          {MENU_GROUPS.map(({ group, titleKey, isPersonal }) => {
            const groupItems = visibleMenuItems.filter(
              (item) => item.group === group
            );
            if (groupItems.length === 0) return null;

            return (
              <div
                key={group}
                className={
                  isPersonal
                    ? "menu-group-personal mx-2 mb-2 rounded-lg bg-white/[0.08] pb-2 ring-1 ring-white/10"
                    : undefined
                }
              >
                <p
                  id={`menu-group-${group}`}
                  className={
                    isPersonal
                      ? "px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-white/80"
                      : "px-5 pb-1 pt-4 text-xs font-medium text-white/50"
                  }
                >
                  {t(titleKey)}
                </p>
                <ul
                  aria-labelledby={`menu-group-${group}`}
                  className={isPersonal ? "space-y-1 px-1" : "space-y-1 px-2"}
                >
                  {groupItems.map((item) => {
                    const isActive = activeItem === item.id;
                    // A touch screen has no tooltip, so what the ⚡ and the dot mean is also
                    // written under the name (below 1024px, where the sidebar is a drawer).
                    const hint = [
                      item.hasPersonal && unreadKudosLabel,
                      item.needsAttention && needsAttentionLabel,
                    ]
                      .filter(Boolean)
                      .join(" · ");

                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => handleMenuClick(item.url)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 max-lg:min-h-11 rounded-sm transition-all border-none outline-none ${
                            isActive
                              ? "bg-white/10 text-white active"
                              : "bg-transparent text-white/70"
                          }`}
                          style={{
                            background: isActive
                              ? "rgba(255, 255, 255, 0.1)"
                              : "transparent",
                            border: "none",
                            outline: "none",
                          }}
                        >
                          {/* Render Polaris icon using Icon component */}
                          <div
                            className={`w-4 h-4 flex-shrink-0 flex items-center justify-center ${
                              isActive ? "text-white" : "text-white/70"
                            }`}
                          >
                            {item.icon && (
                              <Icon source={item.icon as any} tone="base" />
                            )}
                          </div>
                          <span className="min-w-0 flex-1 text-left">
                            <span
                              className={`block text-sm break-words ${
                                isActive ? "text-white" : "text-white/70"
                              }`}
                            >
                              {item.label}
                            </span>
                            {hint && (
                              <span
                                aria-hidden="true"
                                className="block truncate text-[11px] text-amber-300 lg:hidden"
                              >
                                {hint}
                              </span>
                            )}
                          </span>
                          {item.hasPersonal ? (
                            <span
                              className="flex-shrink-0 text-base leading-none"
                              title={unreadKudosLabel}
                              aria-label={unreadKudosLabel}
                            >
                              ⚡
                            </span>
                          ) : null}
                          {item.needsAttention ? (
                            <span
                              role="img"
                              className="h-2 w-2 flex-shrink-0 rounded-full bg-amber-400"
                              title={needsAttentionLabel}
                              aria-label={needsAttentionLabel}
                            />
                          ) : null}
                          {item.badge != null && item.badge > 0 ? (
                            <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full">
                              {item.badge > 99 ? "99+" : item.badge}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header
          className="dashboard-topbar-slot bg-white border-b border-gray-200 flex items-center justify-between px-2 md:px-6 shrink-0"
          style={{ borderColor: brandColors.form.border }}
        >
          {/* Left: Sidebar toggle */}
          <button
            onClick={handleSidebarToggle}
            className={`p-2 border-none bg-transparent hover:bg-gray-100 rounded-sm transition-colors focus:outline-none ${TOPBAR_ICON_BUTTON_TOUCH_TARGET}`}
            aria-label="Toggle sidebar"
          >
            {isSidebarOpen ? (
              <X className="w-5 h-5 text-gray-600" />
            ) : (
              <Menu className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {/* Right: assistant, cart, notifications & user profile */}
          <div className="flex items-center gap-1 lg:gap-3">
            {/* New version available: only the icon below 768px, the long text does not fit there */}
            {updateAvailable && (
              <button
                onClick={handleRefresh}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 max-md:min-h-11 max-md:min-w-11 max-md:px-0 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md border-none transition-colors focus:outline-none"
                title={t("new_version_available")}
              >
                <RefreshCw className="w-4 h-4" />
                <span className="max-md:sr-only">{t("new_version_available")}</span>
              </button>
            )}

            {!isGroupVisitor && (
              <>
                {onlineUsersButton}

                {/* AI assistant (Tomas) — Master-only, renders nothing otherwise */}
                <AssistantLauncher />

                {/* Document download cart */}
                <CartBadge />

                {/* Notifications: a guest member has no tasks to be told about */}
                {!isGuestMember && (
                  <button
                    onClick={onNotificationClick}
                    className={`relative p-2 border-none bg-transparent hover:bg-gray-100 rounded-sm transition-colors focus:outline-none ${TOPBAR_ICON_BUTTON_TOUCH_TARGET}`}
                    title={notificationCount > 0 ? notificationsLabel : undefined}
                    aria-label={notificationsLabel}
                  >
                    <Bell className="w-5 h-5 text-gray-600" />
                    <HeaderBadge count={notificationCount} />
                  </button>
                )}
              </>
            )}

            {/* User Profile - Render the Polaris UserMenu */}
            <div className="flex items-center">{userMenuMarkup}</div>
          </div>
        </header>
        {!isGroupVisitor && <CartDrawer />}

        {/* Page Content. lg:pr-16 leaves room for the online-users rail, which a visitor does not get */}
        <main
          className={`dashboard-main flex-1 overflow-auto bg-gray-50 pb-[var(--phone-bar-height,0px)] ${
            isGroupVisitor ? "" : "lg:pr-16"
          }`}
          data-scroll-restore="main"
        >
          {/* Overlay for mobile when sidebar is open */}
          {isSidebarOpen && isMobile && (
            <div
              className="fixed inset-0 bg-black/50 z-[45] lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
          {isMaster && companyId && !isGroupVisitor && (
            <MicrosoftSecretExpiryBanner companyId={companyId} />
          )}
          {children}
        </main>
        {showPhoneBar && (
          <PhoneBottomBar
            menuItems={visibleMenuItems}
            activeItemId={activeItem}
            isMenuOpen={isSidebarOpen}
            onOpenMenu={() => setIsSidebarOpen(true)}
            onNavigate={handleMenuClick}
          />
        )}
      </div>
    </div>
  );
}
